import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Chatbot from 'react-chatbot-kit';
import 'react-chatbot-kit/build/main.css';
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import ActionProvider from '../../components/chatbot/ActionProvider';
import config from '../../components/chatbot/config';
import MessageParser from '../../components/chatbot/MessageParser';
import { DataGrid } from '../../components/ui/DataGrid';
import { FiltersBar } from '../../components/ui/FiltersBar';
import { MultiQuestionsFilterComponent } from '../../components/ui/FiltersBar/components/MultiQuestionsFilterComponent';
import { FontAwesomeIcon } from '../../components/ui/FontAwesomeIcon';
import { HttpRequest } from '../../util/http';
import { IFilters, userData } from '../../util/userData';
import { LocalContextProvider } from './LocalContext';
import { generateCSVFromComments } from './utils';

export interface IComment {
    id: string;       // internal row id used by selection
    text: string;     // the comment text
    extref: string;   // <-- NEW: external reference (original API id)
    survey: string;   // survey abbreviation/name
}

export const SmartSummariesContainer: React.FC = () => {
    const [comments, setComments] = useState<IComment[]>([]);
    const [selectedComments, setSelectedComments] = useState<IComment[]>([]);
    const [selectedCommentIDs, setSelectedCommentIDs] = useState<any[]>([]);
    const [filters, setFilters] = useState<IFilters>();
    const abortControllerRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasNoData, setHasNoData] = useState(false);
    const [isChatFullscreen, setIsChatFullscreen] = useState(false);

    // Add/remove fullscreen class to override chatbot height
    useEffect(() => {
        if (isChatFullscreen) {
            document.body.classList.add('chatbot-fullscreen');
        } else {
            document.body.classList.remove('chatbot-fullscreen');
        }
        return () => {
            document.body.classList.remove('chatbot-fullscreen');
        };
    }, [isChatFullscreen]);

    const renderCheckbox = () => {
        return (record: IComment) => {
            const isCommentSelected = selectedCommentIDs.includes(record.id);
            return (
                <i
                    className={isCommentSelected ? 'fas fa-check-square' : 'far fa-square'}
                    onClick={() => {
                        if (isCommentSelected) {
                            setSelectedCommentIDs(selectedCommentIDs.filter((value) => value !== record.id));
                            setSelectedComments(selectedComments.filter((comment) => comment.id !== record.id));
                        } else {
                            setSelectedCommentIDs([...selectedCommentIDs, record.id]);
                            setSelectedComments([...selectedComments, record]);
                        }
                    }}
                />
            );
        };
    };

    const renderExtref = () => {
        return (record: IComment) => {
            return <span>{record.extref}</span>;
        };
    };

    const renderComment = () => {
        return (record: IComment) => {
            return <span>{record.text.slice(0, 75)} {record.text.length > 75 ? '...' : ''}</span>;
        };
    };

    userData.filtersListen((updatedFilters) => {
        setFilters(updatedFilters);
    }, false);

    // Reference to store the previous filter
    const prevFilterRef = useRef<any>(null);

    // Debounced backend call. To debounce ensures we wait before making the call.
    const debouncedBackendCall = React.useMemo(
        () =>
            _.debounce((updatedFilter) => {
                setIsLoading(true);
                // If there's an existing request in-flight, abort it
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }

                // Create a new abort controller for this request
                const abortController = new AbortController();
                abortControllerRef.current = abortController;

                const method = 'POST';
                const data = updatedFilter;

                HttpRequest.fetch('/web/v1/comments/get-by-filters', { method, data, signal: abortController.signal })
                    .then((res: any) => {
                        // keep your guard style
                        if (res && res.data) {

                            if (res.data.comments && res.data.comments.length > 0) {
                                setHasNoData(false);

                                // filter out qids containing TEXT, then map
                                const rows = res.data.comments
                                    .filter(function (c: any) {
                                        var q = (c && c.qid) ? String(c.qid) : '';
                                        return q.toUpperCase().indexOf('TEXT') === -1;
                                    })
                                    .reduce(function (acc: IComment[], comment: any) {
                                        acc.push({
                                            id: String(comment.id),          // internal id used by selection
                                            extref: String(comment.id),      // <-- NEW: show original API id in grid
                                            text: String(comment.comment || ''),
                                            survey: String(comment.study || '')  // survey abbreviation (from 'study' field)
                                        });
                                        return acc;
                                    }, []);

                                setComments(rows);
                            } else {
                                setHasNoData(true);
                                setComments([]);
                            }
                            setIsLoading(false);
                        }
                    })
                    .catch((error: any) => {
                        if (error && error.name === 'AbortError') {
                            // The request was aborted, ignore this error silently
                        } else {
                            // Handle other errors
                            // tslint:disable-next-line:no-console
                            console.error('Fetch error:', error);
                        }
                        setIsLoading(false);
                    });
            }, 750),
        []
    );

    useEffect(() => {
        // On first render, prevFilterRef.current will be null, so just store the current filter.
        if (prevFilterRef.current === null) {
            prevFilterRef.current = filters;
            return;
        }

        // Compare current filters with previous filters
        if (!_.isEqual(filters, prevFilterRef.current)) {
            // If different, run the debounced call
            debouncedBackendCall(filters);
            // Update the reference to the latest filters
            prevFilterRef.current = filters;
        }
    }, [filters, debouncedBackendCall]);

    useEffect(() => {
        return () => {
            // On component unmount, abort any ongoing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const generateCSV = generateCSVFromComments(comments);

    return (
        <div>
            <style>{`
                .chatbot-fullscreen .react-chatbot-kit-chat-container {
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                }
                .chatbot-fullscreen .react-chatbot-kit-chat-inner-container {
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                }
                .chatbot-fullscreen .react-chatbot-kit-chat-message-container {
                    flex: 1 !important;
                    height: auto !important;
                    max-height: none !important;
                    overflow-y: auto !important;
                }
                .chatbot-fullscreen .react-chatbot-kit-chat-input-container {
                    flex-shrink: 0 !important;
                }
            `}</style>
            <FiltersBar
                hideMetrics
                hideQuestions
                hideCommentTypes
                hideSearch
                showMonthPicker
                showMultiQuestionsFilter
                useNewContainer
            >
                <MultiQuestionsFilterComponent />
            </FiltersBar>
            <Container fluid className="mt-5" style={{ position: 'relative' }}>
                <Row style={{ position: 'relative' }}>
                    <Col sm={7} style={isChatFullscreen ? { visibility: 'hidden' } : {}}>
                        <Card className="bg-white">
                            <CardHeader className="bg-white">
                                <h4>
                                    Comments &nbsp;
                                    {isLoading &&
                                        <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin muted />
                                    }
                                    {!isLoading && comments.length > 0 &&
                                        <span className="badge right">
                                            <span>{comments.length} comment{comments.length > 1 ? 's' : ''} </span>
                                        </span>
                                    }
                                </h4>
                            </CardHeader>
                            <CardBody className="bg-white">
                                {hasNoData &&
                                    <Alert color="warning" className="animated fadeIn">
                                        <FontAwesomeIcon icon="sticky-note" verticalAlign="initial" size="md" />
                                        <span>&nbsp;No data found for the current filters.</span>
                                    </Alert>
                                }
                                {(!hasNoData && !isLoading) &&
                                    <DataGrid<IComment>
                                        hideInputs={comments.length <= 10}
                                        columns={[
                                            {
                                                header: 'Survey ID', sortable: false, width: 45, render: renderExtref(),   // <-- NEW COLUMN
                                            },
                                            {
                                                header: 'Comment', sortable: false, width: 900, render: renderComment(),
                                            },
                                        ]}
                                        records={comments}
                                    />
                                }
                            </CardBody>
                        </Card>
                    </Col>
                    <Col sm={isChatFullscreen ? 12 : 5} style={{ position: isChatFullscreen ? 'absolute' : 'relative', top: 0, left: 0, right: 0, zIndex: isChatFullscreen ? 1000 : 'auto' }}>
                        <Card
                            className="bg-white"
                            style={isChatFullscreen ? {
                                minHeight: 'calc(100vh - 250px)',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease'
                            } : {
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <CardHeader className="bg-white" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                <h5 style={{ margin: 0 }}>Smart Summaries (AI Chat Agent)</h5>
                                <span
                                    onClick={() => setIsChatFullscreen(!isChatFullscreen)}
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '24px',
                                        color: '#666',
                                        userSelect: 'none'
                                    }}
                                    title={isChatFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                                >
                                    {isChatFullscreen ? '✕' : '⛶'}
                                </span>
                            </CardHeader>
                            <CardBody
                                className="bg-white"
                                style={isChatFullscreen ? {
                                    flex: 1,
                                    overflow: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column'
                                } : {}}
                            >
                                <div style={isChatFullscreen ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}}>
                                    <LocalContextProvider value={{ content: { comments, selectedComments } }}>
                                        <Chatbot
                                            config={config}
                                            messageParser={MessageParser}
                                            actionProvider={ActionProvider}
                                            headerText="Smart Summaries (AI Chat Agent)" // Change this text
                                        />
                                    </LocalContextProvider>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};
